import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, QueryList, ViewChildren, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatChipListbox, MatChipOption, MatChipAvatar } from '@angular/material/chips';
import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { ENTER } from '@angular/cdk/keycodes';
import { timer } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { AutoUnsubscribe } from '@shared/helpers/auto-unsubscribe';
import { BuscaGlobalService } from './busca-global.service';
import { ItemDeBuscaDirective } from './item-de-busca.directive';
import { GrupoDeResultado, MINIMO_DE_CARACTERES, ResultadoDeBusca, TIPOS, TipoDeBusca } from './busca-global.model';

/**
 * A paleta de comando da barra superior — forma de `referencia/pesquisa.png`.
 *
 * ## O que o Material já entrega, e por isso NÃO está aqui (Lei 1)
 *
 * `MatDialog` monta o `Overlay` do CDK com backdrop, prende o foco pelo
 * `ConfigurableFocusTrap` (o mesmo motor do `cdkTrapFocus`), fecha no `Esc` e
 * no clique do backdrop, DEVOLVE o foco ao gatilho e escreve `role="dialog"` +
 * `aria-modal`. Nada disso é código nosso. Posição e largura vêm da
 * `MatDialogConfig` de quem abre (header), não de CSS.
 *
 * ## O debounce não é enfeite
 *
 * Cada tecla custaria até QUATRO chamadas. `debounceTime(750)` é o mesmo valor
 * que `kt-common-filters` usa para busca remota (`common-filters.component.ts:76`)
 * — seguir o número da casa evita ter dois "tempos de espera" no produto.
 * `distinctUntilChanged` corta a repetição do mesmo termo (apagar e redigitar
 * a mesma letra) e `switchMap` CANCELA a busca anterior: sem ele, a resposta
 * lenta de "sil" chegaria depois da de "silva" e sobrescreveria o resultado
 * certo pelo errado.
 *
 * ## OnPush
 *
 * O resultado chega por `subscribe`, fora de qualquer ligação de template, e
 * por isso `markForCheck()` é obrigatório na chegada — sem ele a lista fica
 * parada.
 */
@Component({
  selector: 'mg-busca-global',
  templateUrl: './busca-global.component.html',
  styleUrls: ['./busca-global.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatIcon, MatChipListbox, MatChipOption, MatChipAvatar, ItemDeBuscaDirective],
})
export class BuscaGlobalComponent extends AutoUnsubscribe implements AfterViewInit {
  private readonly busca = inject(BuscaGlobalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogo = inject(MatDialogRef<BuscaGlobalComponent, ResultadoDeBusca>);

  @ViewChildren(ItemDeBuscaDirective) linhas!: QueryList<ItemDeBuscaDirective>;

  private teclado?: ActiveDescendantKeyManager<ItemDeBuscaDirective>;

  readonly campo = new FormControl('', { nonNullable: true });

  /**
   * Os chips que este usuário pode ver. `menu` entra sempre — todo mundo tem
   * pelo menos um item de menu; os quatro de entidade vêm do MESMO gate do
   * serviço, então quem não vê fornecedor não vê nem o chip "Fornecedores".
   */
  readonly chips: readonly TipoDeBusca[] = [...this.busca.tiposPermitidos(), 'menu'];

  grupos: readonly GrupoDeResultado[] = [];
  tipoSelecionado: TipoDeBusca | null = null;
  buscando = false;
  buscaConcluida = false;

  readonly rotuloDe = (tipo: TipoDeBusca) => TIPOS[tipo].rotulo;
  readonly iconeDe = (tipo: TipoDeBusca) => TIPOS[tipo].icone;

  /** O template precisa do mesmo número que o serviço usa — nunca um literal solto. */
  readonly minimo = MINIMO_DE_CARACTERES;

  constructor() {
    super();

    this.sub = this.campo.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(termo => {
          this.buscaConcluida = false;
          this.buscando = termo.trim().length >= this.minimo;
          this.cdr.markForCheck();
        }),
        // A nova digitação cancela imediatamente a busca anterior, inclusive durante o atraso.
        switchMap(termo => timer(750).pipe(switchMap(() => this.busca.buscar(termo, true))))
      )
      .subscribe(grupos => {
        this.buscaConcluida = true;
        this.grupos = grupos;
        this.buscando = false;
        // `detectChanges()`, e não só `markForCheck()`: o efeito colateral
        // desejado é reposicionar o cursor na primeira linha a cada busca
        // nova. Se o `@for` reaproveitar os nós por `track item.id` (mesmo
        // conjunto de ids, resultado repetido), `QueryList.changes` NÃO
        // emite — e essa é a única outra fonte que reseta o cursor
        // (`ngAfterViewInit`, para o caso em que os ids realmente mudam antes
        // do `linhas.changes` disparar). Renderizar aqui garante que a
        // `QueryList` reflita o resultado atual antes de reposicionar.
        this.cdr.detectChanges();
        this.teclado?.setFirstItemActive();
      });
  }

  /** O que a lista mostra depois do recorte por chip. */
  get gruposVisiveis(): readonly GrupoDeResultado[] {
    return this.tipoSelecionado ? this.grupos.filter(g => g.tipo === this.tipoSelecionado) : this.grupos;
  }

  /** Clicar no chip já marcado desmarca — é o comportamento de `mat-chip-listbox`. */
  alternarTipo(tipo: TipoDeBusca): void {
    this.tipoSelecionado = this.tipoSelecionado === tipo ? null : tipo;
    this.cdr.markForCheck();
  }

  /** O destino é decidido por quem abriu (header): a paleta só devolve a escolha. */
  escolher(resultado: ResultadoDeBusca): void {
    this.dialogo.close(resultado);
  }

  fechar(): void {
    this.dialogo.close();
  }

  /** O `id` que o campo aponta. `null` quando não há linha ativa. */
  get itemAtivoId(): string | null {
    return this.teclado?.activeItem?.idDoItem ?? null;
  }

  ngAfterViewInit(): void {
    // O construtor aceita `QueryList` SEM injector — só o overload de signal
    // exige um. E ele já reassina sozinho quando a lista muda, que é o caso:
    // a lista se refaz a cada busca.
    this.teclado = new ActiveDescendantKeyManager(this.linhas).withWrap().withHomeAndEnd();

    // A lista nova nasce com a primeira linha ativa: o índice da lista velha
    // apontaria para uma linha que não existe mais.
    this.sub = this.linhas.changes.subscribe(() => {
      this.teclado?.setFirstItemActive();
      this.cdr.markForCheck();
    });

    this.teclado.setFirstItemActive();
    this.cdr.detectChanges();
  }

  /**
   * ↑ ↓ Home End vão para o gerenciador; ↵ é nosso, porque "selecionar" é
   * decisão da paleta, não do CDK. `Esc` NÃO aparece aqui: quem fecha é o
   * `MatDialog` (Lei 1).
   */
  aoTeclar(evento: KeyboardEvent): void {
    if (evento.keyCode === ENTER) {
      const ativo = this.teclado?.activeItem;
      if (ativo) {
        evento.preventDefault();
        this.escolher(ativo.resultado);
      }
      return;
    }

    this.teclado?.onKeydown(evento);
  }
}
